// Static policy renderer. Reused by /privacy, /rules, /editor-code so the
// three legal pages share one layout, one tone, and one style. Markdown
// files live at src/content/policies/*.md and are inlined at build time
// via Vite's `?raw` import — no runtime fetch, always in-cache, no CSP
// gymnastics for a static doc.
//
// If a fourth policy page ever appears (cookie policy, T&Cs), add it via
// its own route pointing at this component.

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { renderMarkdown } from '../lib/markdown';

interface PolicyPageProps {
    /** Kicker eyebrow — e.g. "OnHere · Privacy". */
    kicker:   string;
    /** Main headline. */
    title:    string;
    /** One-line italic subhead under the headline. */
    subtitle: string;
    /** Effective date, ISO-ish (rendered as-is). */
    effective: string;
    /** Raw markdown source. */
    bodyMd:   string;
}

export function PolicyPage({ kicker, title, subtitle, effective, bodyMd }: PolicyPageProps) {
    useEffect(() => { document.title = `${title} · OnHere`; }, [title]);

    return (
        <main className="article-view">
            <div className="article-view-wrap policy-view">
                <Link to="/" className="back-btn">← Back to the paper</Link>

                <div className="av-kicker">{kicker}</div>
                <h1 className="av-headline">{title}</h1>
                <p className="av-subhead">{subtitle}</p>
                <div className="av-byline">Effective {effective}</div>

                <div
                    className="av-body policy-body"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(bodyMd) }}
                />

                <div className="policy-footer">
                    <Link to="/privacy">Privacy</Link>
                    <span aria-hidden> · </span>
                    <Link to="/rules">Community rules</Link>
                    <span aria-hidden> · </span>
                    <Link to="/editor-code">Editor code</Link>
                </div>
            </div>
        </main>
    );
}
